import { IActivity } from '../../data-models';
import { groupPendingDocIdsByCase } from '../../utils/upload-activities';
import { postDocumentRefresh } from '../../utils/web-apis-client';
import { log } from '../../utils/log';
import { addAvailableDocIds, getAvailableDocIds } from './available-doc-ids';

export interface CheckpointDeps {
  refetchActivities: () => Promise<IActivity[]>;
  refreshPersonData: () => Promise<void>;
  getDocumentFileNames: () => ReadonlySet<string>;
}

const refreshCaseDocuments = async (caseId: string, docIds: string[]) => {
  try {
    // undefined (e.g. a 500) means "not yet"; try again next checkpoint
    return (await postDocumentRefresh(caseId, docIds)) ?? [];
  } catch (e) {
    log.error('Upload document refresh failed', e);
    return [];
  }
};

/**
 * One polling checkpoint: re-pulls the upload activities, asks each case with
 * accepted uploads whether their documents are available, and re-pulls
 * personData and activities when any are.
 */
export const runUploadCheckpoint = async ({
  refetchActivities,
  refreshPersonData,
  getDocumentFileNames,
}: CheckpointDeps) => {
  const activities = await refetchActivities();
  const docIdsByCase = groupPendingDocIdsByCase(activities, Date.now(), {
    availableDocIds: getAvailableDocIds(),
    documentFileNames: getDocumentFileNames(),
  });

  const results = await Promise.all(
    [...docIdsByCase].map(([caseId, docIds]) =>
      refreshCaseDocuments(caseId, docIds),
    ),
  );
  const available = results.flat();
  if (!available.length) {
    return;
  }

  addAvailableDocIds(available);
  await Promise.all([refreshPersonData(), refetchActivities()]);
};
